import { useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import { Row, Col, Modal, Form, Slider, Checkbox, Button } from 'antd';

import { FLAT_TYPE_GENERIC } from '../../common/consts';
import {
  formatCurrency,
  formatCurrencyForSlider,
  formatSqrMetersForSlider,
} from '../../common/helpers';
import { enumToArray } from '../../common/helpers/enum.utils';
import { getFlatTypeLabel } from '../../common/helpers/flatType.utils';
import { extractFlatTypeFromQuery } from '../../common/helpers/searchQuery.utils';
import useI18n from '../../common/hooks/useI18n';
import { SearchFilter } from '../../common/hooks/useSearchService';
import { FlatType } from '../../common/model/enums/flatType.enum';

type Props = {
  visible: boolean;
  onOk: (filter: SearchFilter) => void;
  onCancel: () => void;
};

const typesDefaultValue = [];
export const typesOptions = enumToArray(FlatType, 'string', 'label', 'value');
export const priceMin = 30000;
export const priceMax = 200000;
export const sqrMetersMin = 50;
export const sqrMetersMax = 300;
const roomsDefaultValue: string[] = [];
const roomsOptions = ['1', '2', '3', '4', '+4'];
const bathroomsDefaultValue: string[] = [];
const bathroomsOptions = ['1', '2', '3', '+3'];
const characteristicsDefaultValue: string[] = [];
const characteristicsOptions = [
  'elevator',
  'garage',
  'terrace',
  'swimmingPool',
];

const FullWidthCheckboxGroup = styled(Checkbox.Group)`
  width: 100%;
`;

const SliderFormItem = styled(Form.Item)`
  .ant-form-item-control {
    margin-top: 36px;
    padding-left: 36px;
    padding-right: 32px;
  }
`;

const FiltersModal: React.FC<Props> = ({
  visible,
  onOk,
  onCancel,
}): JSX.Element => {
  const router = useRouter();
  const i18n = useI18n();

  const [form] = Form.useForm();

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const computeFormState = () => {
      let types = form.getFieldValue('types');
      if (router.query.types) {
        types = Array.isArray(router.query.types)
          ? router.query.types
          : [router.query.types];
      } else if (
        router.query.flatType &&
        router.query.flatType !== FLAT_TYPE_GENERIC
      ) {
        types = [extractFlatTypeFromQuery(router.query.flatType as string)];
      }
      const rooms = router.query.rooms
        ? Array.isArray(router.query.rooms)
          ? router.query.rooms
          : [router.query.rooms]
        : form.getFieldValue('rooms');
      if (router.query.roomsMin) {
        rooms.push(roomsOptions[roomsOptions.length - 1]);
      }
      const bathrooms = router.query.bathrooms
        ? Array.isArray(router.query.bathrooms)
          ? router.query.bathrooms
          : [router.query.bathrooms]
        : form.getFieldValue('bathrooms');
      if (router.query.bathroomsMin) {
        bathrooms.push(bathroomsOptions[bathroomsOptions.length - 1]);
      }
      const characteristics = router.query.characteristics
        ? Array.isArray(router.query.characteristics)
          ? router.query.characteristics
          : [router.query.characteristics]
        : form.getFieldValue('characteristics');
      return {
        types,
        price: [
          router.query.priceMin
            ? +router.query.priceMin
            : form.getFieldValue('price')[0],
          router.query.priceMax
            ? +router.query.priceMax
            : form.getFieldValue('price')[1],
        ],
        sqrMeters: [
          router.query.sqrMetersMin
            ? +router.query.sqrMetersMin
            : form.getFieldValue('sqrMeters')[0],
          router.query.sqrMetersMax
            ? +router.query.sqrMetersMax
            : form.getFieldValue('sqrMeters')[1],
        ],
        rooms,
        bathrooms,
        characteristics,
      };
    };

    if (visible) {
      setTimeout(() => form.setFieldsValue(computeFormState()));
    }
  }, [
    form,
    router.query.bathrooms,
    router.query.bathroomsMin,
    router.query.characteristics,
    router.query.flatType,
    router.query.sqrMetersMax,
    router.query.sqrMetersMin,
    router.query.priceMax,
    router.query.priceMin,
    router.query.rooms,
    router.query.roomsMin,
    router.query.types,
    visible,
  ]);

  const onClearFiltersButtonClick = useCallback(() => {
    form.resetFields();
  }, [form]);

  const onApplyFiltersButtonClick = useCallback(() => {
    const generateFilter = (): SearchFilter => {
      const filter: SearchFilter = {};
      if (form.getFieldValue('types')) {
        filter.types = form.getFieldValue('types');
      }
      if (form.getFieldValue('price')) {
        const priceValue: [number, number] = form.getFieldValue('price');
        filter.priceMin =
          priceValue[0] === priceMin ? undefined : priceValue[0];
        filter.priceMax =
          priceValue[1] === priceMax ? undefined : priceValue[1];
      }
      if (form.getFieldValue('sqrMeters')) {
        const sqrMetersValue: [number, number] = form.getFieldValue(
          'sqrMeters'
        );
        filter.sqrMetersMin =
          sqrMetersValue[0] === sqrMetersMin ? undefined : sqrMetersValue[0];
        filter.sqrMetersMax =
          sqrMetersValue[1] === sqrMetersMax ? undefined : sqrMetersValue[1];
      }
      if (form.getFieldValue('rooms')) {
        const roomsValue: string[] = form.getFieldValue('rooms');
        filter.rooms = roomsValue.filter((v) => v !== '+4').map((v) => +v);
        filter.rooms = filter.rooms.length === 0 ? undefined : filter.rooms;
        filter.roomsMin =
          roomsValue.filter((v) => v === '+4').length === 0 ? undefined : 5;
      }
      if (form.getFieldValue('bathrooms')) {
        const bathroomsValue: string[] = form.getFieldValue('bathrooms');
        filter.bathrooms = bathroomsValue
          .filter((v) => v !== '+3')
          .map((v) => +v);
        filter.bathrooms =
          filter.bathrooms.length === 0 ? undefined : filter.bathrooms;
        filter.bathroomsMin =
          bathroomsValue.filter((v) => v === '+3').length === 0 ? undefined : 4;
      }
      if (form.getFieldValue('characteristics')) {
        form.getFieldValue('characteristics');
        filter.characteristics = form.getFieldValue('characteristics');
      }
      return filter;
    };

    onOk(generateFilter());
  }, [form, onOk]);

  const priceTooltipFormatter = useCallback(
    (p: number) => formatCurrencyForSlider(p, priceMin, priceMax, i18n),
    [i18n]
  );

  const sqrMetersTooltipFormatter = useCallback(
    (p: number) =>
      formatSqrMetersForSlider(p, sqrMetersMin, sqrMetersMax, i18n),
    [i18n]
  );

  return (
    <Modal
      title={i18n.t('search.filtersModal.title')}
      open={visible}
      centered
      footer={
        <Row justify="space-between">
          <Col>
            <Button
              htmlType="button"
              type="text"
              onClick={onClearFiltersButtonClick}
            >
              {i18n.t('search.actions.clearFilters')}
            </Button>
          </Col>
          <Col>
            <Button
              htmlType="button"
              type="primary"
              onClick={onApplyFiltersButtonClick}
            >
              {i18n.t('search.actions.applyFilters')}
            </Button>
          </Col>
        </Row>
      }
      onCancel={onCancel}
    >
      <div ref={modalRef}>
        <Form
          form={form}
          layout="vertical"
          name="filters"
          initialValues={{
            types: typesDefaultValue,
            price: [priceMin, priceMax],
            sqrMeters: [sqrMetersMin, sqrMetersMax],
            rooms: roomsDefaultValue,
            bathrooms: bathroomsDefaultValue,
            characteristics: characteristicsDefaultValue,
          }}
        >
          <Form.Item name="types" label={i18n.t('flat.type')}>
            <FullWidthCheckboxGroup>
              <Row>
                {typesOptions
                  .map((o) => ({
                    value: o.value,
                    label: i18n.t(getFlatTypeLabel(o.label as string)),
                  }))
                  .map((o) => (
                    <Col key={o.value} xs={12} sm={8}>
                      <Checkbox value={o.value}>{o.label}</Checkbox>
                    </Col>
                  ))}
              </Row>
            </FullWidthCheckboxGroup>
          </Form.Item>
          <SliderFormItem name="price" label={i18n.t('flat.price')}>
            <Slider
              range
              min={priceMin}
              max={priceMax}
              step={10000}
              marks={{
                [priceMin]: formatCurrency(priceMin, i18n.activeLocale),
                [priceMax]: formatCurrency(priceMax, i18n.activeLocale),
              }}
              tooltip={{
                open: visible,
                formatter: priceTooltipFormatter,
                getPopupContainer: () => modalRef.current,
              }}
            />
          </SliderFormItem>
          <SliderFormItem name="sqrMeters" label={i18n.t('flat.sqrMeters')}>
            <Slider
              range
              min={sqrMetersMin}
              max={sqrMetersMax}
              step={10}
              marks={{
                [sqrMetersMin]: `${sqrMetersMin}m²`,
                [sqrMetersMax]: `${sqrMetersMax}m²`,
              }}
              tooltip={{
                open: visible,
                formatter: sqrMetersTooltipFormatter,
                getPopupContainer: () => modalRef.current,
              }}
            />
          </SliderFormItem>
          <Form.Item name="rooms" label={i18n.t('flat.rooms')}>
            <FullWidthCheckboxGroup>
              <Row justify="space-between">
                {roomsOptions.map((o) => (
                  <Col key={o} span={4}>
                    <Checkbox value={o}>{o}</Checkbox>
                  </Col>
                ))}
              </Row>
            </FullWidthCheckboxGroup>
          </Form.Item>
          <Form.Item name="bathrooms" label={i18n.t('flat.bathrooms')}>
            <FullWidthCheckboxGroup>
              <Row justify="space-between">
                {bathroomsOptions.map((o) => (
                  <Col key={o} span={4}>
                    <Checkbox value={o}>{o}</Checkbox>
                  </Col>
                ))}
              </Row>
            </FullWidthCheckboxGroup>
          </Form.Item>
          <Form.Item
            name="characteristics"
            label={i18n.t('flat.characteristics')}
          >
            <FullWidthCheckboxGroup>
              <Row justify="space-between">
                {characteristicsOptions.map((o) => (
                  <Col
                    key={o}
                    xs={48 / characteristicsOptions.length}
                    sm={24 / characteristicsOptions.length}
                  >
                    <Checkbox value={o}>{i18n.t(`flat.${o}`)}</Checkbox>
                  </Col>
                ))}
              </Row>
            </FullWidthCheckboxGroup>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default FiltersModal;
